"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { auth, db } from "./firebase";
import { setActiveCurrency } from "@eventify/shared/utils/currency";
import { setActiveDemoVendorScope, markScopeResolved } from "@eventify/shared/utils/demoVendorScope";
import { useAuthStore, type User } from "@eventify/shared/state/authStore";
import { useTeamProfilesStore } from "@eventify/shared/state/teamProfilesStore";
import { cleanupLegacyAutoSeededManager } from "@eventify/shared/services/teamProfileService";

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  firebaseUser: null,
  user: null,
  isLoading: true,
  isAuthenticated: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const subscribeTeamProfiles = useTeamProfilesStore((s) => s.subscribe);
  const unsubscribeTeamProfiles = useTeamProfilesStore((s) => s.unsubscribe);

  // Subscribe to team profiles. Team Profiles are opt-in — solo vendors have
  // ZERO profiles. The cleanup call removes leftover auto-seeded Manager docs
  // from when the feature shipped with eager seeding; it's a no-op for anyone
  // who has set a PIN, added teammates, or used the new explicit create flow.
  // Mirrors the mobile RootNavigator logic.
  useEffect(() => {
    if (!user || user.role !== "vendor") {
      unsubscribeTeamProfiles();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await cleanupLegacyAutoSeededManager(user.id);
      } catch (err) {
        console.warn("[AuthProvider] cleanupLegacyAutoSeededManager failed:", err);
      }
      if (!cancelled) subscribeTeamProfiles(user.id);
    })();
    return () => {
      cancelled = true;
      unsubscribeTeamProfiles();
    };
  }, [user, subscribeTeamProfiles, unsubscribeTeamProfiles]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        // Fetch user doc from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", fbUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Legacy users may not have hasCompletedOnboarding — treat undefined as true
            // so we don't push existing accounts back through onboarding.
            const hasCompleted =
              data.hasCompletedOnboarding === undefined ? true : !!data.hasCompletedOnboarding;
            // Demo accounts carry currencyOverride: "USD" on their user doc
            // (written only by the demo seed script). Must run BEFORE setUser:
            // all dashboard surfaces gate on user/isLoading, so they first
            // render after this, picking up the right symbol. Module state
            // does not trigger re-renders — that auth gate is what makes a
            // plain setter safe here.
            setActiveCurrency(data.currencyOverride === "USD" ? "USD" : "ILS");
            // Demo customers see ONLY their scope's vendors (no mock, no other
            // real vendors) across browse, discovery, and every Evie tool.
            // Set before setUser so the first vendor fetch is already scoped.
            setActiveDemoVendorScope(data.demoVendorScope ?? null);
            setUser({
              id: fbUser.uid,
              email: fbUser.email || "",
              name: data.name || fbUser.displayName || "",
              role: data.role || "customer",
              createdAt: data.createdAt || new Date().toISOString(),
              profilePicture: data.profilePicture || fbUser.photoURL || undefined,
              hasCompletedOnboarding: hasCompleted,
              // One-time gate for the vendor product tour (/vendor-tour). Only
              // truthy once the vendor has finished/skipped it; missing on
              // everyone else (incl. all customers), which is fine.
              hasSeenVendorTour: !!data.hasSeenVendorTour,
              // Load userType so returning users (especially multi-managers
              // who never have hasCompletedOnboarding=true) don't get bounced
              // back through the account-type screen on every login.
              userType: data.userType,
              // Surfaces the TestModeBanner across all authenticated surfaces
              // (vendor dashboard + event detail). True only when the doc
              // explicitly has _isTest set; missing field stays undefined.
              ...(data._isTest === true ? { _isTest: true } : {}),
            });
            // Attach uid to Sentry events for per-user error filtering.
            // ID only — beforeSend scrubs email/IP as a backstop.
            Sentry.setUser({ id: fbUser.uid });
          } else {
            // User doc doesn't exist yet — new user, will be created during onboarding
            setActiveCurrency("ILS");
            setActiveDemoVendorScope(null);
            logout();
            Sentry.setUser(null);
          }
        } catch (error) {
          console.error("[AuthProvider] Error fetching user doc:", error);
          setActiveCurrency("ILS");
          setActiveDemoVendorScope(null);
          logout();
          Sentry.setUser(null);
        }
      } else {
        setActiveCurrency("ILS");
        setActiveDemoVendorScope(null);
        logout();
        Sentry.setUser(null);
        // Clear the vendor profile on a GENUINE sign-out.
        //
        // Nine web surfaces call firebase `signOut(auth)` directly rather than
        // the shared wrapper in services/firebaseAuth.ts, so none of them
        // cleared this store. A mid-session sign-out left the previous vendor's
        // profile in place marked "loaded", which every capability gate reads
        // as authoritative — so the next person to use the browser could be
        // shown the prior vendor's surfaces, guest counts included.
        //
        // THIS BRANCH ONLY, and that is load-bearing. `logout()` is also called
        // at the two branches above, where the user IS signed in to Firebase
        // but their users/{uid} doc is missing or unreadable — which is exactly
        // what a brand-new account looks like mid-signup. Clearing there is the
        // bug this file's mobile twin already shipped once. Here `fbUser` is
        // null, which only Firebase sign-out produces.
        try {
          const { useServiceProviderStore } = await import(
            "@eventify/shared/state/serviceProviderStore"
          );
          await useServiceProviderStore.getState().switchUser(null, "");
        } catch (err) {
          console.warn("[AuthProvider] could not clear vendor profile:", err);
        }
      }

      setLoading(false);
      // Auth (and thus the demo vendor scope) is now settled in every branch
      // above — unblock any discovery fetch that awaited whenScopeResolved().
      markScopeResolved();
    });

    return () => unsubscribe();
  }, [setUser, logout]);

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        isLoading: loading,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
